import Game from '@/models/Game';
import MatchSide from '@/models/MatchSide';
import {
  TeamMatchFormat,
} from '@/services/utils/enums';

/**
 * Фронтовая модель одного командного матча.
 *
 * TeamMatch не хранится в БД. Он собирается из нескольких Game с одинаковым
 * `teamMatchKey` и представляет один матч вида:
 *   "сторона 1 vs сторона 2"
 *
 * Источник истины по-прежнему остаётся на уровне Game:
 * - каждая партия знает свой стол
 * - каждая партия знает свой результат
 * - каждая партия знает командный контекст
 *
 * TeamMatch агрегирует эти данные, чтобы:
 * - показать матч двух команд как единую сущность
 * - посчитать его досочный счёт и матчевый результат
 * - использовать его далее в командной Series
 */
export default class TeamMatch {
  /**
   * @param {Object} params
   * @param {string|null} [params.teamMatchKey=null] - Ключ группировки Game в один TeamMatch.
   * @param {number|null} [params.round=null] - Тур, поднятый из входящих Game.
   * @param {string|null} [params.stage=null] - Стадия плей-офф, если матч относится к сетке.
   * @param {number|null} [params.stageNumber=null] - Порядковый номер стадии.
   * @param {number|null} [params.bracketIndex=null] - Позиция в сетке.
   * @param {Object|null} [params.side1=null] - Первая сторона матча.
   * @param {Object|null} [params.side2=null] - Вторая сторона матча.
   * @param {Game[]} [params.games=[]] - Партии, входящие в командный матч.
   * @param {'board-sum'|'common-board'|'first-win'} [params.teamMatchFormat='board-sum'] - Формат командного матча.
   * @param {Object|null} [params.score=null] - Досочный счёт командного матча.
   * @param {'1-0'|'0-1'|'½-½'|null} [params.result=null] - Матчевый результат.
   * @param {'pending'|'ongoing'|'finished'} [params.status='pending'] - Статус матча.
   * @param {Object} [params.meta={}] - Служебные данные.
   */
  constructor({
    teamMatchKey = null,
    round = null,
    stage = null,
    stageNumber = null,
    bracketIndex = null,
    side1 = null,
    side2 = null,
    games = [],
    teamMatchFormat = TeamMatchFormat.BOARD_SUM,
    score = null,
    result = null,
    status = 'pending',
    meta = {},
  } = {}) {
    this.teamMatchKey = teamMatchKey;
    this.round = round;
    this.stage = stage;
    this.stageNumber = stageNumber;
    this.bracketIndex = bracketIndex;
    this.side1 = MatchSide.fromObject(side1);
    this.side2 = MatchSide.fromObject(side2);
    this.games = Array.isArray(games) ? games.map((game) => game instanceof Game ? game : Game.fromObject(game)) : [];
    this.teamMatchFormat = teamMatchFormat;
    this.status = status;
    this.meta = meta ?? {};
    this.isTeamBye = this.#calculateIsTeamBye();
    this.isByeGroup = Boolean(this.meta?.isByeGroup);
    this.score = score ?? this.calculateScore();
    this.result = result ?? this.calculateResult();
  }

  static fromObject(obj = {}) {
    return new TeamMatch(obj);
  }

  /**
   * Строит ключ группировки командного матча регулярной стадии.
   * @param {number} round
   * @param {number|null} cycle
   * @param {string|null} side1Id
   * @param {string|null} side2Id
   * @returns {string}
   */
  static buildRegularKey(round, cycle, side1Id, side2Id) {
    const cyclePart = cycle ? `C${cycle}` : 'C0';
    return `REG-${cyclePart}-R${round}-${side1Id ?? 'bye'}-${side2Id ?? 'bye'}`;
  }

  /**
   * Строит ключ группировки командного матча плей-офф.
   * @param {string} stage
   * @param {number|null} stageNumber
   * @param {number|null} level
   * @param {string|null} side1Id
   * @param {string|null} side2Id
   * @param {number|null} [bracketIndex=null]
   * @returns {string}
   */
  static buildEliminationKey(stage, stageNumber, level, side1Id, side2Id, bracketIndex = null) {
    const bracketPart = bracketIndex != null ? `B${bracketIndex}` : 'B0';
    return `ELIM-${stage}-S${stageNumber ?? 1}-L${level ?? 1}-${bracketPart}-${side1Id ?? 'bye'}-${side2Id ?? 'bye'}`;
  }

  /**
   * Строит ключ командного матча для временных random-команд.
   * @param {number|string} round
   * @param {string|null} side1Key
   * @param {string|null} side2Key
   * @returns {string}
   */
  static buildRandomKey(round, side1Key, side2Key) {
    const clean = (key) => String(key || 'bye').replace(new RegExp(`^R${round}-`), '');
    return `R${round}-${clean(side1Key)}-${clean(side2Key)}`;
  }

  /**
   * Вычисляет досочный счёт командного матча по входящим партиям.
   *
   * Поддерживаемые режимы:
   * - `board-sum`   — суммируем очки по всем партиям;
   * - `first-win`   — матч выигрывает сторона, первой получившая победу на доске;
   * - `common-board` сейчас естественно попадает в сценарий одной партии.
   *
   * @returns {{side1:number|null, side2:number|null, isBye:boolean, summary:string}}
   */
  calculateScore() {
    const sortedGames = [...this.games].sort((a, b) => {
      const aBoard = a?.board ?? Number.MAX_SAFE_INTEGER;
      const bBoard = b?.board ?? Number.MAX_SAFE_INTEGER;
      if (aBoard !== bBoard) return aBoard - bBoard;
      const aCreated = a?.createdAt ?? '';
      const bCreated = b?.createdAt ?? '';
      return aCreated.localeCompare(bCreated);
    });

    if (this.teamMatchFormat === TeamMatchFormat.FIRST_WIN) {
      for (const game of sortedGames) {
        const winnerSide = this.#getWinnerSideForGame(game);
        if (winnerSide) {
          return {
            side1: winnerSide === 'side1' ? 1 : 0,
            side2: winnerSide === 'side2' ? 1 : 0,
            isBye: false,
            summary: winnerSide === 'side1' ? '1-0' : '0-1',
          };
        }
      }

      if (sortedGames.length > 0 && sortedGames.every((game) => this.#isGameFinished(game))) {
        return {
          side1: 0.5,
          side2: 0.5,
          isBye: false,
          summary: '0.5-0.5',
        };
      }

      return {
        side1: 0,
        side2: 0,
        isBye: false,
        summary: '0-0',
      };
    }

    let side1 = 0;
    let side2 = 0;

    for (const game of sortedGames) {
      const score = this.#getScoreForGame(game);
      side1 += score.side1Score;
      side2 += score.side2Score;
    }

    return {
      side1,
      side2,
      isBye: false,
      summary: `${side1}-${side2}`,
    };
  }

  /**
   * Вычисляет матчевый результат для таблицы: победа, поражение или ничья.
   * Досочный счёт остаётся отдельно в `score`.
   *
   * @returns {'1-0'|'0-1'|'½-½'|null}
   */
  calculateResult() {
    const score = this.score ?? this.calculateScore();

    if (!this.#isResultReady()) return null;

    const side1 = Number(score?.side1 ?? 0);
    const side2 = Number(score?.side2 ?? 0);

    if (side1 > side2) return '1-0';
    if (side2 > side1) return '0-1';
    return '½-½';
  }

  /**
   * Переводит результат одной партии в очки для сторон TeamMatch.
   * @param {Game} game
   * @returns {{side1Score:number, side2Score:number}}
   */
  #getScoreForGame(game) {
    const result = game?.result;
    const whiteBelongsToSide1 = this.#isWhiteTeamSide1(game);

    if (result === '1-0') {
      return whiteBelongsToSide1
        ? { side1Score: 1, side2Score: 0 }
        : { side1Score: 0, side2Score: 1 };
    }

    if (result === '0-1') {
      return whiteBelongsToSide1
        ? { side1Score: 0, side2Score: 1 }
        : { side1Score: 1, side2Score: 0 };
    }

    if (result === '½-½') {
      return { side1Score: 0.5, side2Score: 0.5 };
    }

    return { side1Score: 0, side2Score: 0 };
  }

  /**
   * Возвращает победившую сторону по одной партии.
   * @param {Game} game
   * @returns {'side1'|'side2'|null}
   */
  #getWinnerSideForGame(game) {
    const { side1Score, side2Score } = this.#getScoreForGame(game);
    if (side1Score > side2Score) return 'side1';
    if (side2Score > side1Score) return 'side2';
    return null;
  }

  #isResultReady() {
    if (this.teamMatchFormat === TeamMatchFormat.FIRST_WIN) {
      const hasWinner = this.games.some((game) => Boolean(this.#getWinnerSideForGame(game)));
      return hasWinner || this.games.every((game) => this.#isGameFinished(game));
    }

    return this.games.length > 0 && this.games.every((game) => this.#isGameFinished(game));
  }

  #isGameFinished(game) {
    return Boolean(game?.result);
  }

  #calculateIsTeamBye() {
    if (this.meta?.isByeGroup) return false;

    return Boolean(this.side1?.id)
      && !this.side2?.id
      && this.games.length > 0
      && this.games.every((game) => game?.isBye);
  }

  /**
   * Определяет, относится ли белая сторона партии к `side1` командного матча.
   * Использует:
   * - `whiteTeamId/blackTeamId` для fixed teams
   * - `whiteTeamKey/blackTeamKey` для random teams
   *
   * @param {Game} game
   * @returns {boolean}
   */
  #isWhiteTeamSide1(game) {
    const whiteTeamId = game?.getWhiteTeamId?.();
    const blackTeamId = game?.getBlackTeamId?.();
    const whiteTeamKey = game?.getWhiteTeamKey?.();
    const blackTeamKey = game?.getBlackTeamKey?.();

    if (this.side1.entityType === 'team' && whiteTeamId) {
      return this.side1.id === whiteTeamId;
    }

    if (this.side1.entityType === 'team' && blackTeamId) {
      return this.side1.id !== blackTeamId;
    }

    if (this.side1.entityType === 'team-key' && whiteTeamKey) {
      return this.side1.id === whiteTeamKey;
    }

    if (this.side1.entityType === 'team-key' && blackTeamKey) {
      return this.side1.id !== blackTeamKey;
    }

    return true;
  }
}
